import { useState, useEffect } from "react";
import Link from "next/link";
import Head from "next/head";
import { getCollegePosts } from "@/lib/api";
import CounsellingModal from "./CounsellingModal";
import Image from "next/image";
import "rc-slider/assets/index.css";
import University from "../public/images/University.webp";
import CollegeContent from "../../cms-ghost-app/pages/college/CollegeContent.js";
import { FaPaperPlane } from "react-icons/fa6";
import RecentColleges from "@/components/recentpost";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import OneToOneSession from "../components/OneToOneSessionModal";
import {
  faMapMarkerAlt,
  faStar,
  faMoneyBill,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import { FaInfoCircle } from "react-icons/fa";
import { useCart } from "../context/CartContext";
import { useRouter } from "next/router";

const ABROAD_COUNTRY_OPTIONS = [
  "Australia",
  "Bangladesh",
  "Belarus",
  "Canada",
  "China",
  "Georgia",
  "Germany",
  "Guyana",
  "Hungary",
  "Ireland",
  "Japan",
  "Kazakhstan",
  "Kyrgyzstan",
  "Luxembourg",
  "Malaysia",
  "Nepal",
  "Netherlands",
  "New Zealand",
  "Philippines",
  "Romania",
  "Russia",
  "UAE",
  "United Kingdom",
  "United States",
  "Uzbekistan",
  "Vietnam",
];

const COUNTRY_TAG_MAP = {
  australia: "Australia",
  bangladesh: "Bangladesh",
  belarus: "Belarus",
  canada: "Canada",
  china: "China",
  georgia: "Georgia",
  germany: "Germany",
  guyana: "Guyana",
  hungary: "Hungary",
  ireland: "Ireland",
  japan: "Japan",
  kazakhstan: "Kazakhstan",
  kyrgyzstan: "Kyrgyzstan",
  luxembourg: "Luxembourg",
  malaysia: "Malaysia",
  nepal: "Nepal",
  netherlands: "Netherlands",
  "new-zealand": "New Zealand",
  philippines: "Philippines",
  romania: "Romania",
  russia: "Russia",
  uae: "UAE",
  "united-kingdom": "United Kingdom",
  "united-states": "United States",
  uzbekistan: "Uzbekistan",
  vietnam: "Vietnam",
};

const COUNTRY_NAME_TO_TAG = Object.fromEntries(
  Object.entries(COUNTRY_TAG_MAP).map(([tag, name]) => [
    name.toLowerCase(),
    tag,
  ])
);

const ABROAD_COUNTRY_SET = new Set(
  ABROAD_COUNTRY_OPTIONS.map((country) => country.toLowerCase())
);

const COLLEGE_LIST_FILTER_STORAGE_KEY = "collegeListLocationFilters";

const normalizeValue = (value) => (value || "").trim().toLowerCase();

const normalizeTag = (value) =>
  normalizeValue(value)
    .replace(/\s+/g, "-")
    .replace(/&/g, "and");

const sortOptionsWithIndiaOnTop = (options = []) => {
  const uniqueOptions = [
    ...new Set(options.filter((option) => option && option !== "MCC")),
  ];

  const indiaOption = uniqueOptions.find(
    (option) => option.trim().toLowerCase() === "india"
  );

  const otherOptions = uniqueOptions
    .filter((option) => option.trim().toLowerCase() !== "india")
    .sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" }));

  return indiaOption ? [indiaOption, ...otherOptions] : otherOptions;
};

const parseCollegeData = (college) => {
  const excerpt = college?.custom_excerpt;

  if (!excerpt || typeof excerpt !== "string") {
    return {
      courseOffered: ["MBBS"],
    };
  }

  const trimmedExcerpt = excerpt.trim();

  /*
    Some Ghost posts have custom_excerpt as normal text:
    Example: "Discover MBBS in Russia..."
    That is not JSON, so we must not run JSON.parse on it.
  */
  if (!trimmedExcerpt.startsWith("{") && !trimmedExcerpt.startsWith("[")) {
    return {
      plainExcerpt: trimmedExcerpt,
      courseOffered: ["MBBS"],
    };
  }

  try {
    const parsedData = JSON.parse(trimmedExcerpt);

    return {
      courseOffered: ["MBBS"],
      ...parsedData,
    };
  } catch (error) {
    console.warn(
      `Invalid custom_excerpt JSON for college: ${
        college?.title || "Unknown College"
      }`
    );

    return {
      plainExcerpt: trimmedExcerpt,
      courseOffered: ["MBBS"],
    };
  }
};

const getGhostTagSlugs = (college) => {
  if (!college?.tags || !Array.isArray(college.tags)) {
    return [];
  }

  return college.tags
    .map((tag) => tag.slug || tag.name)
    .filter(Boolean)
    .map(normalizeTag);
};

const getCountryFromTags = (college) => {
  const tagSlugs = getGhostTagSlugs(college);
  const matchedTag = tagSlugs.find((tagSlug) => COUNTRY_TAG_MAP[tagSlug]);

  return matchedTag ? COUNTRY_TAG_MAP[matchedTag] : "";
};

const getCollegeCountry = (collegeData, college) => {
  return (
    collegeData?.country ||
    collegeData?.countryName ||
    collegeData?.countryOrRegion ||
    collegeData?.region ||
    getCountryFromTags(college) ||
    ""
  );
};

const isAbroadCollege = (collegeData, college) => {
  const country = getCollegeCountry(collegeData, college);

  if (country) {
    return normalizeValue(country) !== "india";
  }

  const location = collegeData?.location || "";
  return ABROAD_COUNTRY_SET.has(normalizeValue(location));
};

const getFilterLocation = (collegeData, college) =>
  collegeData?.location || getCollegeCountry(collegeData, college) || "";

const getCollegeCutoff = (college) => {
  const collegeData = parseCollegeData(college);
  const cutoff = Number(collegeData?.cutoff);

  return Number.isFinite(cutoff) && cutoff > 0
    ? cutoff
    : Number.MAX_SAFE_INTEGER;
};

const formatCourse = (courseOffered) => {
  if (Array.isArray(courseOffered)) {
    return courseOffered.join(", ");
  }

  if (typeof courseOffered === "string" && courseOffered.trim()) {
    return courseOffered;
  }

  return "MBBS";
};

const Dropdown = ({ label, options, onSelect, value, keepIndiaOnTop = false }) => {
  const uniqueOptions = keepIndiaOnTop
    ? sortOptionsWithIndiaOnTop(options)
    : [...new Set(options.filter((option) => option && option !== "MCC"))].sort(
        (a, b) => a.localeCompare(b, "en", { sensitivity: "base" })
      );

  return (
    <div className="relative inline-block text-left w-full md:w-48">
      <select
        className="w-full border border-[#ff9245] sm:px-4 sm:py-2 px-3 py-2 rounded-full appearance-none pr-8 sm:text-sm text-xs scrollbar-custom"
        onChange={(e) => onSelect(e.target.value)}
        value={value}
      >
        <option value="">{label}</option>

        {uniqueOptions.map((option, index) => (
          <option key={`${option}-${index}`} value={option}>
            {option}
          </option>
        ))}
      </select>

      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    </div>
  );
};

const CollegeCard = ({ college, onViewDetails }) => {
  const { addToCart } = useCart();

  const [collegeData, setCollegeData] = useState({
    courseOffered: ["MBBS"],
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setCollegeData(parseCollegeData(college));
  }, [college]);

  const displayLocation = getFilterLocation(collegeData, college);

  return (
    <>
      <div
        className="border rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow duration-300 w-full max-w-[350px] mx-auto flex flex-col h-full cursor-pointer"
        onClick={() => onViewDetails(college.slug)}
      >
        <img
          src={college.feature_image || "/default.jpg"}
          alt={college.title || "College"}
          width={350}
          height={160}
          className="rounded-lg w-full h-40 object-fill"
        />

        <div className="mt-4 flex-grow">
          <h3 className="text-sm md:text-md font-semibold line-clamp-2 overflow-hidden text-ellipsis whitespace-normal">
            {college.title}
          </h3>

          <p className="text-xs sm:text-sm text-gray-500">
            Course: {formatCourse(collegeData?.courseOffered)}
          </p>

          <div className="mt-3 flex justify-between items-start">
            <ul className="text-xs sm:text-sm text-gray-500 space-y-2">
              <li className="flex items-center">
                <FontAwesomeIcon
                  icon={faStar}
                  className="mr-2 text-yellow-500 w-4 h-4"
                />
                {collegeData?.reviewCount || "0"} Reviews
              </li>

              <li className="flex items-center">
                <FontAwesomeIcon
                  icon={faMapMarkerAlt}
                  className="mr-2 text-[#005F59] w-4 h-4"
                />
                {displayLocation || "Unknown"}
              </li>
            </ul>

            <ul className="text-xs sm:text-sm text-gray-500 space-y-2">
              <li className="flex items-center">
                <FontAwesomeIcon
                  icon={faMoneyBill}
                  className="mr-2 text-green-500 w-4 h-4"
                />
                Fees: {collegeData?.fees || "N/A"}
              </li>

              <li className="flex items-center">
                <FontAwesomeIcon
                  icon={faChartLine}
                  className="mr-2 text-blue-500 w-4 h-4"
                />
                Cutoff: {collegeData?.cutoff || "N/A"}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-4 flex justify-between items-center">
          <button
            className="relative text-[#ff9245] px-3 py-1 !ring-1 ring-[#ff9245] rounded-full hover:text-[#fff] hover:bg-[#ff9245] transition-colors text-xs md:text-sm duration-300"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(college.slug);
            }}
          >
            View Details
          </button>

          <button
            className="px-3 py-1 text-xs md:text-sm rounded-full bg-[#ff9245] text-white border border-[#ff9245] hover:bg-white hover:!text-[#ff9245] hover:border-[#ff9245] hover:ring-1 hover:ring-[#ff9245] transition-colors duration-300 flex items-center"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsModalOpen(true);
            }}
          >
            <FaPaperPlane className="mr-2" />
            <span> Start Application</span>
          </button>
        </div>
      </div>

      <CounsellingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        college={college}
        course={collegeData?.courseOffered}
        location={displayLocation || "Not Available"}
      />
    </>
  );
};

const CollegeList = ({ allCollegePosts }) => {
  const router = useRouter();

  const postsPerPage = 9;

  const countryOptions = sortOptionsWithIndiaOnTop([
    "India",
    ...ABROAD_COUNTRY_OPTIONS,
  ]);

  const [showOneToOneModal, setShowOneToOneModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("India");
  const [selectedCollege, setSelectedCollege] = useState("");
  const [selectedRating, setSelectedRating] = useState("");
  const [selectedCutoff, setSelectedCutoff] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);

  const handleViewDetails = (slug) => {
    sessionStorage.setItem(
      COLLEGE_LIST_FILTER_STORAGE_KEY,
      JSON.stringify({
        country: selectedCountry,
        state: selectedState,
        page: currentPage,
      })
    );
    router.push(`/college/${slug}`);
  };

  useEffect(() => {
    const savedFilters = sessionStorage.getItem(
      COLLEGE_LIST_FILTER_STORAGE_KEY
    );

    if (!savedFilters) return;

    try {
      const { country, state, page } = JSON.parse(savedFilters);

      setSelectedCountry(country || "India");
      setSelectedState(state || "");
      setCurrentPage(Number(page) || 1);
      sessionStorage.removeItem(COLLEGE_LIST_FILTER_STORAGE_KEY);
    } catch (error) {
      console.warn("Unable to restore college list filters:", error);
      sessionStorage.removeItem(COLLEGE_LIST_FILTER_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowOneToOneModal(true);
    }, 8000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const states = new Set();

    allCollegePosts.forEach((college) => {
      const collegeData = parseCollegeData(college);

      if (!isAbroadCollege(collegeData, college) && collegeData?.location) {
        states.add(collegeData.location.trim());
      }
    });

    setStateOptions(Array.from(states).filter(Boolean).sort());
  }, [allCollegePosts]);

  useEffect(() => {
    if (searchQuery) {
      const results = allCollegePosts.filter((post) =>
        post.title?.toLowerCase().includes(searchQuery.toLowerCase())
      );

      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, allCollegePosts]);

  const filteredPosts = allCollegePosts.filter((college) => {
    const collegeData = parseCollegeData(college);

    const collegeCountry = getCollegeCountry(collegeData, college) || "";
    const collegeLocation = getFilterLocation(collegeData, college) || "";
    const collegeIsAbroad = isAbroadCollege(collegeData, college);
    const collegeTagSlugs = getGhostTagSlugs(college);

    let countryPass = true;

    if (selectedCountry) {
      const selectedCountryLower = selectedCountry.trim().toLowerCase();

      if (selectedCountryLower === "india") {
        countryPass = !collegeIsAbroad;
      } else {
        const selectedCountryTag =
          COUNTRY_NAME_TO_TAG[selectedCountryLower] ||
          normalizeTag(selectedCountryLower);

        countryPass =
          normalizeValue(collegeCountry) === selectedCountryLower ||
          normalizeValue(collegeLocation) === selectedCountryLower ||
          collegeTagSlugs.includes(selectedCountryTag);
      }
    }

    const statePass =
      selectedCountry && selectedCountry.toLowerCase() === "india"
        ? !selectedState || collegeLocation === selectedState
        : true;

    return (
      countryPass &&
      statePass &&
      college.title?.toLowerCase().includes(searchQuery.toLowerCase()) &&
      (selectedCollege
        ? college.title?.toLowerCase().includes(selectedCollege.toLowerCase())
        : true) &&
      (selectedRating
        ? Number(collegeData?.rating) >= Number(selectedRating)
        : true) &&
      (selectedCutoff
        ? Number(collegeData?.cutoff) <= Number(selectedCutoff)
        : true)
    );
  });

  const sortedPosts = filteredPosts
    .slice()
    .sort((a, b) => getCollegeCutoff(a) - getCollegeCutoff(b));

  const startIdx = (currentPage - 1) * postsPerPage;
  const currentPosts = sortedPosts.slice(startIdx, startIdx + postsPerPage);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const heroPost = currentPosts[0];

  const handleSearch = () => {
    if (searchQuery) {
      window.open(
        `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(
          searchQuery
        )}`,
        "_blank"
      );
    }
  };

  const handleStateChange = (state) => {
    setSelectedState(state);
    setCurrentPage(1);
  };

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setSelectedState("");
    setCurrentPage(1);
  };

  return (
    <>
      <OneToOneSession
        isOpen={showOneToOneModal}
        onClose={() => setShowOneToOneModal(false)}
      />

      <Head>
        <title>
          Top Medical Colleges in India and Abroad NEET 2026: NEET Support
        </title>

        <link rel="shortcut icon" href="/favicon.ico" />

        <meta
          name="description"
          content="Everything about the top medical colleges in India and abroad, from government to private MBBS colleges, explore detailed information to choose the best one for NEET 2026."
        />

        <meta
          name="keywords"
          content="top medical colleges in india, top medical colleges abroad, top government medical colleges in india, private medical colleges in india, college list, courses, rank, cut-off, admission, location, neet 2026"
        />

        <meta
          property="og:title"
          content="List of Top Medical Colleges in India and Abroad 2026: NEET Support"
        />

        <meta
          property="og:description"
          content="Check out top medical colleges in India and abroad, including government and private MBBS colleges with all the details you need to make the right choice."
        />

        <meta
          property="og:image"
          content={heroPost?.feature_image || "/default.jpg"}
        />

        <meta
          property="og:url"
          content={typeof window !== "undefined" ? window.location.href : ""}
        />

        <link rel="canonical" href="https://neetsupport.com/collegeList" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `
            {
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "name": "Top Medical Colleges in India and Abroad for NEET 2026",
              "description": "Everything about the top medical colleges in India and abroad, from government to private MBBS colleges. Explore details to choose the best one for NEET 2026.",
              "url": "https://neetsupport.com/collegeList"
            }
            `,
          }}
        />
      </Head>

      <section className="flex-col flex items-center md:justify-between mt-16 mb-16 md:mb-12">
        <div className="relative w-full h-[380px] flex items-center justify-center">
          <div className="absolute inset-0">
            <Image
              src={University}
              alt="University"
              fill
              sizes="100vw"
              className="absolute inset-0 object-cover"
            />

            <div className="absolute inset-0 bg-gray-600 bg-opacity-50"></div>
          </div>

          <div className="absolute inset-0"></div>

          <div className="relative z-10 text-center text-white px-4">
            <h1 className="text-xl md:text-2xl lg:text-3xl xl:text-3xl font-bold">
              Compare and Choose the Best Medical Colleges in India and Abroad
              for NEET 2026
            </h1>

            <p className="text-sm md:text-sm lg:text-lg xl:text-lg mt-2">
              Access detailed information on rankings, fees, eligibility, and
              admission cutoffs—all in one place. Make informed choices,
              discover top institutions, and take the next step toward your
              medical career.
            </p>

            <div className="mt-6 flex justify-center">
              <div className="max-w-xs md:max-w-lg w-full relative">
                <input
                  type="text"
                  placeholder="Search College...."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-6 py-4 rounded-full text-black shadow-lg focus:outline-none"
                />

                <button
                  className="absolute !rounded-full right-2 top-1/2 transform -translate-y-1/2"
                  onClick={handleSearch}
                ></button>

                {searchResults.length > 0 && (
                  <div className="mt-1 bg-white text-black z-10 rounded-lg shadow-lg p-4 max-w-xs md:max-w-lg mx-auto absolute top-full left-0 right-0 max-h-60 overflow-y-auto">
                    <h3 className="text-lg font-semibold mb-2 text-[#ff9245]">
                      Search Results:
                    </h3>

                    <ul>
                      {searchResults.map((result) => (
                        <li key={result.id} className="mb-2">
                          <Link href={`/college/${result.slug}`}>
                            <span className="text-gray-700 text-sm hover:underline hover:text-blue-500">
                              {result.title}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-center space-x-1 sm:space-x-4">
              <button
                className="bg-[#ff9245] hover:ring-[#fff] hover:bg-[#fff] hover:!text-[#ff9245] text-white px-4 py-2 rounded-full text-[10px] sm:text-xs font-semibold transition"
                onClick={() =>
                  (window.location.href = "/neet-college-predictor")
                }
              >
                College Predictor
              </button>

              <button
                className="bg-[#ff9245] hover:ring-[#fff] hover:bg-[#fff] hover:!text-[#ff9245] text-white px-4 py-2 rounded-full text-[10px] sm:text-xs font-semibold transition"
                onClick={() => (window.location.href = "/neet-rank-predictor")}
              >
                Rank Predictor
              </button>

              <button
                className="bg-[#ff9245] hover:ring-[#fff] hover:bg-[#fff] hover:!text-[#ff9245] text-white px-4 py-2 rounded-full text-[10px] sm:text-xs font-semibold transition"
                onClick={() =>
                  (window.location.href =
                    "https://portal.neetsupport.com/dashboard-Counselling")
                }
              >
                Personalized Counselling
              </button>
            </div>
          </div>
        </div>

        <div className="p-8">
          <div className="mb-6 grid grid-cols-1 gap-4 p-4 md:grid-cols-[1fr_auto_1fr] md:items-center">
            <div className="flex flex-wrap items-center gap-3 md:justify-self-start">
              <span className="font-semibold text-xs sm:text-[15px] text-black mr-2">
                {filteredPosts.length > 0 ? (
                  <>
                    Showing{" "}
                    <span className="text-[#005F59] font-semibold">
                      {filteredPosts.length} records
                    </span>
                  </>
                ) : (
                  <>No record found.</>
                )}
              </span>

              {filteredPosts.length > 0 && (
                <FaInfoCircle className="text-[#005F59] text-lg" />
              )}
            </div>

            <div className="flex justify-center md:justify-self-center w-full max-w-xs">
              <Dropdown
                label="Select country"
                options={countryOptions}
                onSelect={handleCountryChange}
                value={selectedCountry}
                keepIndiaOnTop={true}
              />
            </div>

            <div className="md:justify-self-end">
              {selectedCountry &&
              selectedCountry.toLowerCase() === "india" ? (
                <Dropdown
                  label="Select state"
                  options={stateOptions}
                  onSelect={handleStateChange}
                  value={selectedState}
                />
              ) : null}
            </div>
          </div>

          <h2 className="text-center text-lg md:text-2xl font-bold mb-6 text-[#ff9245]">
            Top Medical Colleges
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {currentPosts.length > 0 ? (
              currentPosts.map((college) => (
                <CollegeCard
                  key={college.id}
                  college={college}
                  onViewDetails={handleViewDetails}
                />
              ))
            ) : (
              <div className="text-center col-span-full">
                No colleges found. Try searching different colleges.
              </div>
            )}
          </div>

          <div className="flex justify-center mt-8 items-center space-x-1">
            <button
              className={`px-3 py-1 rounded-full ${
                currentPage === 1
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-gray-200 text-gray-700 hover:bg-[#ff9245] hover:text-white"
              }`}
              onClick={() => {
                if (currentPage > 1) {
                  setCurrentPage(currentPage - 1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
              disabled={currentPage === 1}
              aria-label="Previous Page"
            >
              &lt;
            </button>

            {(() => {
              const pages = [];
              let start = Math.max(1, currentPage - 2);
              let end = Math.min(totalPages, start + 4);

              if (end - start < 4) {
                start = Math.max(1, end - 4);
              }

              for (let i = start; i <= end; i++) {
                pages.push(
                  <button
                    key={i}
                    className={`mx-1 px-3 py-1 rounded-full ${
                      currentPage === i
                        ? "bg-[#ff9245] text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-[#ff9245] hover:text-white"
                    }`}
                    onClick={() => {
                      setCurrentPage(i);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    {i}
                  </button>
                );
              }

              return pages;
            })()}

            <button
              className={`px-3 py-1 rounded-full ${
                currentPage === totalPages || totalPages === 0
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-gray-200 text-gray-700 hover:bg-[#ff9245] hover:text-white"
              }`}
              onClick={() => {
                if (currentPage < totalPages) {
                  setCurrentPage(currentPage + 1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
              disabled={currentPage === totalPages || totalPages === 0}
              aria-label="Next Page"
            >
              &gt;
            </button>
          </div>
        </div>

        <div>
          <RecentColleges
            tag="college"
            onCardClick={(slug) => router.push(`/college/${slug}`)}
            gridCols={3}
          />
        </div>

        <CollegeContent />
      </section>
    </>
  );
};

export async function getStaticProps({ preview }) {
  const allCollegePosts = (await getCollegePosts(preview)) || [];

  allCollegePosts.forEach((post) => {
    if (post.feature_image) {
      post.feature_image = post.feature_image.replace(
        /ghost\.neetsupport\.com/g,
        "neetsupport.com"
      );
    }
  });

  return {
    props: {
      allCollegePosts,
    },
    revalidate: 10,
  };
}

export default CollegeList;
